import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';

import App from './app/App';
import { AuthProvider } from './shared/auth/AuthProvider';
import { TenantProvider } from './shared/tenant/TenantProvider';
import { ThemeProvider } from './shared/utilities/ThemeProvider';
import { ErrorFallback } from './shared/components/feedback/ErrorFallback';
import './i18n';
import './index.css';

// Create query client with defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: (failureCount, error) => {
        // Don't retry on 4xx errors
        if (error && typeof error === 'object' && 'status' in error) {
          const status = (error as { status: number }).status;
          if (status >= 400 && status < 500) return false;
        }
        return failureCount < 3;
      },
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: false,
    },
  },
});

// Error handler for error boundary
const handleError = (error: Error, info: React.ErrorInfo) => {
  console.error('Application error:', error, info);
  // In production, send to error tracking service
};

// Get basename from Vite's base URL (set via --base flag)
// For preview deployment at /app-v2, this will be '/app-v2'
// For production at root, this will be '/'
const basename = import.meta.env.BASE_URL.replace(/\/$/, '') || '/';

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleError}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter basename={basename}>
          <ThemeProvider>
            <AuthProvider>
              <TenantProvider>
                <App />
              </TenantProvider>
            </AuthProvider>
          </ThemeProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
